import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "otpCheckbox", "webauthnCheckbox",
    "otpModal", "webauthnModal", "removeKeyModal", "removeKeyConfirmBtn",
    "otpModalTitle", "otpStep1", "otpStep2", "otpQr", "otpCodeInput", "otpError", "otpConfirmBtn",
    "otpBackupCodes",
    "otpDisableStep", "otpDisableCodeInput", "otpDisableError", "otpDisableBtn",
    "webauthnNickname", "webauthnError", "webauthnRegisterBtn",
    "webauthnModalTitle", "webauthnAddStep", "webauthnDisableStep", "webauthnDisableError", "webauthnDisableBtn"
  ]

  static values = {
    otpEnabled: Boolean,
    webauthnEnabled: Boolean,
    otpSetupUrl: String,
    otpConfirmUrl: String,
    otpDisableUrl: String,
    otpDisableTitle: String,
    otpSetupTitle: String,
    webauthnOptionsUrl: String,
    webauthnRegisterUrl: String,
    webauthnDisableUrl: String,
    webauthnDisableTitle: String,
    webauthnAddTitle: String,
    webauthnAlreadyRegisteredMessage: String
  }

  _csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
  }

  _headers() {
    const h = { 'Content-Type': 'application/json', 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' }
    const token = this._csrfToken()
    if (token) h['X-CSRF-Token'] = token
    return h
  }

  _showModal(modalElement) {
    const B = window.bootstrap
    if (!B?.Modal) return
    if (typeof B.Modal.getOrCreateInstance === 'function') {
      B.Modal.getOrCreateInstance(modalElement).show()
    } else {
      new B.Modal(modalElement).show()
    }
  }

  _hideModal(modalElement) {
    const B = window.bootstrap
    if (!B?.Modal) return
    const instance = B.Modal.getInstance(modalElement)
    if (instance) instance.hide()
  }

  openRemoveKeyModal(event) {
    event.preventDefault()
    const url = event.currentTarget.getAttribute('data-url')
    if (!url) return
    this._pendingRemoveUrl = url
    this._showModal(this.removeKeyModalTarget)
  }

  async confirmRemoveKey() {
    if (!this._pendingRemoveUrl) return
    this.removeKeyConfirmBtnTarget.disabled = true
    try {
      const res = await fetch(this._pendingRemoveUrl, {
        method: 'DELETE',
        headers: this._headers(),
        redirect: 'follow'
      })
      if (res.redirected) {
        window.location.href = res.url
      } else if (res.ok) {
        window.location.reload()
      } else {
        const data = await res.json().catch(() => ({}))
        this.removeKeyConfirmBtnTarget.disabled = false
        this._hideModal(this.removeKeyModalTarget)
        alert(data.error || 'Failed.')
      }
    } catch (err) {
      this.removeKeyConfirmBtnTarget.disabled = false
      this._hideModal(this.removeKeyModalTarget)
      alert(err.message || 'Failed.')
    }
    this._pendingRemoveUrl = null
  }

  openOtpModal(event) {
    event.preventDefault()
    if (this.otpEnabledValue) {
      this._showOtpDisableModal()
      return
    }
    this.otpErrorTarget.textContent = ''
    this.otpCodeInputTarget.value = ''
    this.otpStep1Target.classList.remove('d-none')
    this.otpStep2Target.classList.add('d-none')
    if (this.hasOtpDisableStepTarget) this.otpDisableStepTarget.classList.add('d-none')
    if (this.otpSetupTitleValue) this.otpModalTitleTarget.textContent = this.otpSetupTitleValue
    fetch(this.otpSetupUrlValue, { method: 'GET', headers: this._headers() })
      .then(r => r.json())
      .then(data => {
        if (data.error) {
          this.otpErrorTarget.textContent = data.error
          return
        }
        const img = document.createElement('img')
        img.src = data.qr_data_url
        img.alt = 'QR Code'
        img.className = 'img-fluid'
        this.otpQrTarget.innerHTML = ''
        this.otpQrTarget.appendChild(img)
        this._showModal(this.otpModalTarget)
      })
      .catch(() => { this.otpErrorTarget.textContent = 'Network error.' })
  }

  _showOtpDisableModal() {
    this.otpModalTitleTarget.textContent = this.otpDisableTitleValue || 'Disable OTP'
    this.otpStep1Target.classList.add('d-none')
    this.otpStep2Target.classList.add('d-none')
    this.otpDisableStepTarget.classList.remove('d-none')
    this.otpDisableCodeInputTarget.value = ''
    this.otpDisableErrorTarget.textContent = ''
    this._showModal(this.otpModalTarget)
  }

  otpDisableCodeInput() {
    this.otpDisableErrorTarget.textContent = ''
  }

  async otpDisable() {
    const code = this.otpDisableCodeInputTarget.value.trim()
    if (!code) {
      this.otpDisableErrorTarget.textContent = document.querySelector('[data-i18n-otp-code-required]')?.textContent || 'Enter the code.'
      return
    }
    this.otpDisableBtnTarget.disabled = true
    try {
      const res = await fetch(this.otpDisableUrlValue, {
        method: 'POST',
        headers: this._headers(),
        body: JSON.stringify({ code })
      })
      const data = await res.json().catch(() => ({}))
      this.otpDisableBtnTarget.disabled = false
      if (data.error) {
        this.otpDisableErrorTarget.textContent = data.error
        return
      }
      this._hideModal(this.otpModalTarget)
      window.location.reload()
    } catch (err) {
      this.otpDisableBtnTarget.disabled = false
      this.otpDisableErrorTarget.textContent = err.message || 'Request failed.'
    }
  }

  otpCodeInput() {
    this.otpErrorTarget.textContent = ''
  }

  otpConfirm() {
    const code = this.otpCodeInputTarget.value.trim()
    if (!code) {
      this.otpErrorTarget.textContent = document.querySelector('[data-i18n-otp-code-required]')?.textContent || 'Enter the code.'
      return
    }
    this.otpConfirmBtnTarget.disabled = true
    fetch(this.otpConfirmUrlValue, {
      method: 'POST',
      headers: this._headers(),
      body: JSON.stringify({ code })
    })
      .then(r => r.json())
      .then(data => {
        this.otpConfirmBtnTarget.disabled = false
        if (data.error) {
          this.otpErrorTarget.textContent = data.error
          return
        }
        this.otpStep1Target.classList.add('d-none')
        this.otpStep2Target.classList.remove('d-none')
        this.otpBackupCodesTarget.textContent = (data.backup_codes || []).join('\n')
      })
      .catch(() => {
        this.otpConfirmBtnTarget.disabled = false
        this.otpErrorTarget.textContent = 'Request failed.'
      })
  }

  otpModalDone() {
    window.location.reload()
  }

  openWebauthnModal(event) {
    event.preventDefault()
    this.webauthnErrorTarget.textContent = ''
    this.webauthnNicknameTarget.value = ''
    if (this.webauthnEnabledValue) {
      this._showWebauthnDisableModal()
      return
    }
    if (this.webauthnModalTitleTarget) this.webauthnModalTitleTarget.textContent = this.webauthnAddTitleValue || 'Add hardware key'
    if (this.hasWebauthnAddStepTarget) this.webauthnAddStepTarget.classList.remove('d-none')
    if (this.hasWebauthnDisableStepTarget) this.webauthnDisableStepTarget.classList.add('d-none')
    if (this.hasWebauthnRegisterBtnTarget) this.webauthnRegisterBtnTarget.classList.remove('d-none')
    this._showModal(this.webauthnModalTarget)
  }

  _showWebauthnDisableModal() {
    if (this.webauthnModalTitleTarget) this.webauthnModalTitleTarget.textContent = this.webauthnDisableTitleValue || 'Disable hardware keys'
    if (this.hasWebauthnAddStepTarget) this.webauthnAddStepTarget.classList.add('d-none')
    if (this.hasWebauthnDisableStepTarget) this.webauthnDisableStepTarget.classList.remove('d-none')
    if (this.hasWebauthnRegisterBtnTarget) this.webauthnRegisterBtnTarget.classList.add('d-none')
    this.webauthnDisableErrorTarget.textContent = ''
    this._showModal(this.webauthnModalTarget)
  }

  async confirmWebauthnDisable() {
    this.webauthnDisableBtnTarget.disabled = true
    this.webauthnDisableErrorTarget.textContent = ''
    try {
      const res = await fetch(this.webauthnDisableUrlValue, {
        method: 'POST',
        headers: this._headers()
      })
      const data = await res.json().catch(() => ({}))
      this.webauthnDisableBtnTarget.disabled = false
      if (data.error) {
        this.webauthnDisableErrorTarget.textContent = data.error
        return
      }
      this._hideModal(this.webauthnModalTarget)
      window.location.reload()
    } catch (err) {
      this.webauthnDisableBtnTarget.disabled = false
      this.webauthnDisableErrorTarget.textContent = err.message || 'Request failed.'
    }
  }

  async webauthnRegister() {
    const nickname = this.webauthnNicknameTarget.value.trim()
    this.webauthnErrorTarget.textContent = ''
    this.webauthnRegisterBtnTarget.disabled = true
    try {
      const optionsRes = await fetch(this.webauthnOptionsUrlValue, {
        method: 'POST',
        headers: this._headers(),
        body: JSON.stringify({})
      })
      const options = await optionsRes.json()
      if (options.error) {
        this.webauthnErrorTarget.textContent = options.error
        this.webauthnRegisterBtnTarget.disabled = false
        return
      }
      const credential = await navigator.credentials.create({
        publicKey: this._webauthnCreationOptions(options)
      })
      if (!credential) {
        this.webauthnRegisterBtnTarget.disabled = false
        return
      }
      const body = {
        credential: this._credentialToJSON(credential),
        nickname: nickname || undefined
      }
      const regRes = await fetch(this.webauthnRegisterUrlValue, {
        method: 'POST',
        headers: this._headers(),
        body: JSON.stringify(body)
      })
      const result = await regRes.json()
      this.webauthnRegisterBtnTarget.disabled = false
      if (result.error) {
        this.webauthnErrorTarget.textContent = result.error
        return
      }
      this._hideModal(this.webauthnModalTarget)
      window.location.reload()
    } catch (err) {
      const msg = err?.message ?? ''
      const isAlreadyRegistered =
        /already registered|credentials already registered|Try a different device|already registered this device/i.test(msg)
      this.webauthnErrorTarget.textContent = isAlreadyRegistered && this.webauthnAlreadyRegisteredMessageValue
        ? this.webauthnAlreadyRegisteredMessageValue
        : (msg || 'Failed.')
      this.webauthnRegisterBtnTarget.disabled = false
    }
  }

  _webauthnCreationOptions(options) {
    const o = { ...options }
    if (o.challenge != null) o.challenge = this._toBuffer(o.challenge)
    if (o.user?.id != null) o.user = { ...o.user, id: this._toBuffer(o.user.id) }
    if (Array.isArray(o.excludeCredentials)) {
      o.excludeCredentials = o.excludeCredentials.map(c => ({
        ...c,
        id: this._toBuffer(c.id)
      }))
    }
    return o
  }

  _toBuffer(value) {
    if (typeof value === 'string') {
      return this._base64UrlDecode(value)
    }
    if (Array.isArray(value) || ArrayBuffer.isView(value)) {
      return new Uint8Array(value)
    }
    return value
  }

  _base64UrlDecode(str) {
    if (typeof str !== 'string') return str
    let base64 = str.replace(/-/g, '+').replace(/_/g, '/')
    const pad = base64.length % 4
    if (pad) base64 += (pad === 2 ? '==' : '=')
    return Uint8Array.from(atob(base64), c => c.charCodeAt(0))
  }

  _credentialToJSON(credential) {
    return {
      id: credential.id,
      rawId: this._base64UrlEncode(credential.rawId),
      type: credential.type,
      response: {
        clientDataJSON: this._base64UrlEncode(credential.response.clientDataJSON),
        attestationObject: this._base64UrlEncode(credential.response.attestationObject)
      }
    }
  }

  _base64UrlEncode(buf) {
    const base64 = btoa(String.fromCharCode(...new Uint8Array(buf)))
    return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
  }

}
